/**
 * AssetPreviewModal — fullscreen modal for expanded file preview.
 *
 * Shows the AssetPreview component in a large overlay with filename header.
 * Closes via X button, Escape key, or backdrop click.
 */

import { useEffect } from 'react'
import { X } from 'lucide-react'
import type { Asset } from '../../types/fileManager'
import AssetPreview from './AssetPreview'

interface AssetPreviewModalProps {
  asset: Asset
  onClose: () => void
}

export default function AssetPreviewModal({ asset, onClose }: AssetPreviewModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        onClose()
      }
    }
    // Use capture phase so this fires before the panel's Escape handler
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [onClose])

  return (
    <div className="rq-modal-overlay" onClick={onClose}>
      <div
        className="bg-[var(--rq-surface)] rounded-xl border border-white/10 shadow-2xl flex flex-col"
        style={{ width: '90vw', maxWidth: '1200px', height: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-sm font-semibold text-[var(--rq-text)] truncate">{asset.original_filename}</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-[var(--rq-text-soft)] flex-shrink-0">
              {asset.file_type.split('/').pop()}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview body */}
        <div className="flex-1 min-h-0 p-4 flex">
          <AssetPreview asset={asset} compact={false} />
        </div>
      </div>
    </div>
  )
}
